import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/api';
import { fmtDateLong } from '../services/dates';
import './UsersPage.css';

const ROLE_LABEL = {
  superadmin:        'Superadmin',
  admin:             'Admin',
  comercial:         'Comercial',
  'empresa-cliente': 'Empresa cliente',
};
const ROLE_COLOR = {
  superadmin:        'up-badge--super',
  admin:             'up-badge--admin',
  comercial:         'up-badge--comercial',
  'empresa-cliente': 'up-badge--empresa',
};

const ALL_MODULES = [
  { key: 'crm',           label: 'CRM Leads' },
  { key: 'calculadora',   label: 'Calculadora' },
  { key: 'financiacion',  label: 'Financiación' },
  { key: 'financiero',    label: 'Financiero' },
  { key: 'automatizacion',label: 'Automatizaciones' },
  { key: 'proformas',     label: 'Proformas' },
  { key: 'estadisticas',  label: 'Estadísticas' },
  { key: 'fichaje',       label: 'Fichaje' },
  { key: 'instaladores',  label: 'App Instaladores' },
];

function fmtDate(s) { return fmtDateLong(s); }

// ── Modal crear/editar usuario ────────────────────────────────────────────────
function UserModal({ user, subTenants, tenants, targetRole, isSuperadmin, onClose, onSaved, token }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    // Datos de contacto del usuario
    name:          user?.name          || '',
    email:         user?.email         || '',
    phone:         user?.phone         || '',
    password:      '',
    role:          user?.role          || targetRole,
    sub_tenant_id: user?.sub_tenant_id ?? '',
    tenant_id:     user?.tenant_id     ?? '',
    active:        user?.active        ?? 1,
    // Datos de empresa (superadmin creando/editando admin)
    company_name:  user?.company_data?.nombre   || '',
    nif:           user?.company_data?.nif       || '',
    direccion:     user?.company_data?.direccion || '',
    cp:            user?.company_data?.cp        || '',
    ciudad:        user?.company_data?.ciudad    || '',
    telefono:      user?.company_data?.telefono  || '',
    email_empresa: user?.company_data?.email     || '',
    web:           user?.company_data?.web       || '',
  });
  const [selectedModules, setSelectedModules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null); // { url, email, subdomain }

  const isAdminRole   = form.role === 'admin';
  const isEmpresaRole = form.role === 'empresa-cliente';

  // Precargar módulos del tenant (creando: desde selector; editando: desde user.tenant_id)
  useEffect(() => {
    if (!isSuperadmin || !isAdminRole) { setSelectedModules([]); return; }
    const tid = form.tenant_id || user?.tenant_id;
    if (!tid) { setSelectedModules([]); return; }
    const t = tenants.find(x => String(x.id) === String(tid));
    setSelectedModules(t?.modules || []);
  }, [form.tenant_id, isSuperadmin, isAdminRole, tenants]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  function toggleModule(key) {
    setSelectedModules(prev => prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key]);
  }

  async function handleSave() {
    if (!form.name || !form.email || (!isEdit && !form.password)) {
      alert('Nombre, email y contraseña son obligatorios'); return;
    }
    // Superadmin creando admin nuevo → necesita datos de empresa
    if (isSuperadmin && isAdminRole && !isEdit && !form.company_name.trim()) {
      alert('El nombre comercial de la empresa es obligatorio'); return;
    }
    // Superadmin creando comercial/empresa-cliente → necesita seleccionar tenant
    if (isSuperadmin && !isAdminRole && !form.tenant_id) {
      alert('Selecciona la empresa (tenant) a la que pertenece este usuario'); return;
    }
    setSaving(true);
    try {
      const body = {
        name:   form.name,
        email:  form.email,
        phone:  form.phone || null,
        role:   form.role,
        active: form.active,
      };
      if (form.password) body.password = form.password;

      if (isSuperadmin) {
        if (isAdminRole && !isEdit) {
          // Crear admin + tenant nuevo: enviar datos de empresa y módulos
          body.company_name   = form.company_name.trim();
          body.nif            = form.nif            || null;
          body.direccion      = form.direccion      || null;
          body.cp             = form.cp             || null;
          body.ciudad         = form.ciudad         || null;
          body.empresa_tel    = form.telefono       || null;
          body.empresa_email  = form.email_empresa  || null;
          body.web            = form.web            || null;
          body.modules      = selectedModules;
        } else {
          // Editar admin existente o crear comercial/empresa-cliente en tenant existente
          body.tenant_id = form.tenant_id || user?.tenant_id;
          if (isAdminRole) {
            body.modules       = selectedModules;
            body.company_name  = form.company_name  || null;
            body.nif           = form.nif           || null;
            body.direccion     = form.direccion     || null;
            body.cp            = form.cp            || null;
            body.ciudad        = form.ciudad        || null;
            body.empresa_tel   = form.telefono      || null;
            body.empresa_email = form.email_empresa || null;
            body.web           = form.web           || null;
          }
        }
      } else {
        // Admin crea comercial o empresa-cliente
        body.sub_tenant_id = form.sub_tenant_id || null;
      }

      if (isEdit) {
        await apiPut(`/users/${user.id}`, body, token);
      } else {
        const result = await apiPost('/users', body, token);
        if (result.pending_verification) {
          if (result.email_sent) {
            alert(`✅ Usuario creado. Se ha enviado un email de verificación a ${form.email}.`);
            onSaved();
          } else {
            // Email no enviado — mostrar enlace en la UI
            setVerifyResult({ url: result.verify_url, email: form.email, subdomain: result.subdomain || null });
            onSaved(); // refrescar lista pero mantener modal abierto con el enlace
          }
          return;
        }
      }
      onSaved();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  // Panel enlace verificación (cuando el email no se pudo enviar)
  if (verifyResult) {
    return (
      <div className="up-overlay" onClick={onClose}>
        <div className="up-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
          <div className="up-modal-head">
            <h3>✅ Usuario creado</h3>
            <button className="up-modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="up-modal-body" style={{ padding: '1.25rem' }}>
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 12 }}>
              El usuario <strong>{verifyResult.email}</strong> ha sido creado correctamente pero el email de verificación no se pudo enviar automáticamente.
            </p>
            <p style={{ fontSize: 14, color: 'var(--text)', marginBottom: 8, fontWeight: 600 }}>
              Comparte este enlace con el usuario para que active su cuenta:
            </p>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 12, wordBreak: 'break-all', color: 'var(--muted)', marginBottom: 12 }}>
              {verifyResult.url}
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(verifyResult.url); }}
              style={{ background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer', marginBottom: verifyResult.subdomain ? 12 : 0 }}
            >
              Copiar enlace
            </button>
            {verifyResult.subdomain && (
              <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 12 }}>
                Empresa (subdominio): <strong style={{ color: 'var(--text)' }}>{verifyResult.subdomain}</strong>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="up-overlay" onClick={onClose}>
      <div className="up-modal up-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="up-modal-head">
          <h3>{isEdit ? 'Editar usuario' : 'Nuevo usuario'}</h3>
          <button className="up-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="up-modal-body">

          {/* ── Sección: Datos de contacto del administrador ── */}
          <div className="up-section-title">Datos de contacto</div>
          <div className="up-form-grid">
            <div className="up-field">
              <label>Nombre completo *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Nombre del administrador" />
            </div>
            <div className="up-field">
              <label>Email (login) *</label>
              <input value={form.email} onChange={e => set('email', e.target.value)} type="email"
                placeholder="admin@empresa.com" disabled={isEdit} />
            </div>
            <div className="up-field">
              <label>Teléfono</label>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="6XX XXX XXX" />
            </div>
            <div className="up-field">
              <label>{isEdit ? 'Nueva contraseña (vacío = sin cambios)' : 'Contraseña *'}</label>
              <input value={form.password} onChange={e => set('password', e.target.value)}
                type="password" placeholder="••••••••" />
            </div>

            {/* Rol */}
            <div className="up-field">
              <label>Rol</label>
              {isSuperadmin ? (
                <select value={form.role} onChange={e => set('role', e.target.value)} disabled={isEdit}>
                  <option value="admin">Admin</option>
                  <option value="comercial">Comercial</option>
                  <option value="empresa-cliente">Empresa cliente</option>
                </select>
              ) : (
                <select value={form.role} onChange={e => set('role', e.target.value)} disabled={isEdit}>
                  <option value="comercial">Comercial</option>
                  <option value="empresa-cliente">Empresa cliente</option>
                </select>
              )}
            </div>

            {/* Superadmin + NO admin: selector de tenant existente */}
            {isSuperadmin && !isAdminRole && (
              <div className="up-field">
                <label>Empresa (tenant) *</label>
                <select value={form.tenant_id} onChange={e => set('tenant_id', e.target.value)}>
                  <option value="">— Selecciona empresa —</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name} ({t.subdomain})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Solo superadmin asigna empresa-cliente a un admin */}
            {isSuperadmin && isEmpresaRole && (
              <div className="up-field">
                <label>Empresa cliente asignada</label>
                <select value={form.sub_tenant_id} onChange={e => set('sub_tenant_id', e.target.value)}>
                  <option value="">— Sin asignar —</option>
                  {subTenants.map(st => (
                    <option key={st.id} value={st.id}>{st.name}</option>
                  ))}
                </select>
              </div>
            )}

            {isEdit && (
              <div className="up-field">
                <label>Estado</label>
                <select value={form.active} onChange={e => set('active', parseInt(e.target.value))}>
                  <option value={1}>Activo</option>
                  <option value={0}>Desactivado</option>
                </select>
              </div>
            )}
          </div>

          {/* ── Sección: Datos de empresa — superadmin creando o editando admin ── */}
          {isSuperadmin && isAdminRole && (
            <>
              <div className="up-section-title up-section-title--mt">Datos de la empresa</div>
              <div className="up-form-grid">
                <div className="up-field up-field--full">
                  <label>Nombre comercial *</label>
                  <input value={form.company_name} onChange={e => set('company_name', e.target.value)}
                    placeholder="Ej: Central Vallas PVC" />
                </div>
                <div className="up-field">
                  <label>CIF / NIF</label>
                  <input value={form.nif} onChange={e => set('nif', e.target.value)} placeholder="B12345678" />
                </div>
                <div className="up-field up-field--full">
                  <label>Dirección</label>
                  <input value={form.direccion} onChange={e => set('direccion', e.target.value)} placeholder="Calle Mayor 1" />
                </div>
                <div className="up-field">
                  <label>Código postal</label>
                  <input value={form.cp} onChange={e => set('cp', e.target.value)} placeholder="46000" />
                </div>
                <div className="up-field">
                  <label>Ciudad</label>
                  <input value={form.ciudad} onChange={e => set('ciudad', e.target.value)} placeholder="Valencia" />
                </div>
                <div className="up-field">
                  <label>Teléfono empresa</label>
                  <input value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="96X XXX XXX" />
                </div>
                <div className="up-field">
                  <label>Email empresa</label>
                  <input value={form.email_empresa} onChange={e => set('email_empresa', e.target.value)}
                    type="email" placeholder="info@empresa.com" />
                </div>
                <div className="up-field">
                  <label>Web (opcional)</label>
                  <input value={form.web} onChange={e => set('web', e.target.value)} placeholder="https://empresa.com" />
                </div>
              </div>
            </>
          )}

          {/* Módulos — solo cuando superadmin y rol = admin */}
          {isSuperadmin && isAdminRole && (
            <div className="up-modules-box">
              <div className="up-modules-title">Módulos activos para esta empresa</div>
              <div className="up-modules-grid">
                {ALL_MODULES.map(m => (
                  <label key={m.key} className="up-module-check">
                    <input type="checkbox" checked={selectedModules.includes(m.key)}
                      onChange={() => toggleModule(m.key)} />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="up-modal-foot">
          <button className="up-btn up-btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="up-btn up-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando…' : (isEdit ? 'Guardar cambios' : 'Crear usuario')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function UsersPage() {
  const { auth } = useContext(AuthContext);
  const token        = auth.token;
  const isSuperadmin = auth.user?.role === 'superadmin';

  const [tab,        setTab]        = useState(isSuperadmin ? 'admins' : 'comerciales');
  const [users,      setUsers]      = useState([]);
  const [allUsers,   setAllUsers]   = useState([]);
  const [subTenants, setSubTenants] = useState([]);
  const [tenants,    setTenants]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null);
  const [verifyPanel,  setVerifyPanel]  = useState(null); // { url, email }
  const [resending,    setResending]    = useState(null); // userId en curso
  const [resendResult, setResendResult] = useState(null); // { userId, ok, error }
  const [resetPanel,   setResetPanel]   = useState(null); // { url, email }
  const [resettingPwd, setResettingPwd] = useState(null); // userId en curso
  const [resetResult,  setResetResult]  = useState(null); // { userId, ok, error }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isSuperadmin) {
        const [ten, all] = await Promise.all([
          apiGet('/tenants', token).catch(() => []),
          apiGet('/users/all', token).catch(() => []),
        ]);
        setTenants(ten || []);
        setAllUsers(all || []);
      } else {
        const [u, st] = await Promise.all([
          apiGet('/users', token),
          apiGet('/sub-tenants', token).catch(() => []),
        ]);
        setUsers(u);
        setSubTenants(st);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token, isSuperadmin]);

  useEffect(() => { load(); }, [load]);

  function handleSaved() { setModal(null); load(); }

  async function handleResend(userId, userEmail, copyOnly) {
    setResending(userId);
    setResendResult(null);
    try {
      const result = await apiPost(`/users/${userId}/resend-verification`, {}, token);
      if (copyOnly) {
        setVerifyPanel({ url: result.verify_url, email: userEmail });
      } else if (result.email_sent) {
        setResendResult({ userId, ok: true });
        setTimeout(() => setResendResult(null), 3000);
      } else {
        setResendResult({ userId, ok: false, error: result.email_error || 'Error desconocido' });
      }
    } catch (e) {
      setResendResult({ userId, ok: false, error: e.message });
    } finally {
      setResending(null);
    }
  }

  async function handleSendReset(userId, userEmail, copyOnly) {
    setResettingPwd(userId);
    setResetResult(null);
    try {
      const result = await apiPost(`/users/${userId}/send-reset`, {}, token);
      if (copyOnly) {
        setResetPanel({ url: result.reset_url, email: userEmail });
      } else if (result.email_sent) {
        setResetResult({ userId, ok: true });
        setTimeout(() => setResetResult(null), 3000);
      } else {
        setResetResult({ userId, ok: false, error: result.email_error || 'Error al enviar' });
        setResetPanel({ url: result.reset_url, email: userEmail });
      }
    } catch (e) {
      setResetResult({ userId, ok: false, error: e.message });
    } finally {
      setResettingPwd(null);
    }
  }

  async function handleDelete(u) {
    if (!window.confirm(`¿Eliminar el usuario "${u.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await apiDelete(`/users/${u.id}`, token);
      load();
    } catch (e) {
      alert(e.message || 'Error al eliminar el usuario');
    }
  }

  const isAdmin = auth.user?.role === 'admin';

  // Para superadmin usamos allUsers; para admin usamos users
  const sourceList  = isSuperadmin ? allUsers : users;
  const comerciales = sourceList.filter(u => u.role === 'comercial');
  const empresas    = sourceList.filter(u => u.role === 'empresa-cliente');
  const admins      = sourceList.filter(u => u.role === 'admin');

  // Superadmin ve las 3 pestañas; Admin ve Comerciales + Empresas
  const tabs = isSuperadmin
    ? [
        { key: 'admins',      label: 'Admins',           count: admins.length },
        { key: 'comerciales', label: 'Comerciales',      count: comerciales.length },
        { key: 'empresas',    label: 'Empresas cliente', count: empresas.length },
      ]
    : [
        { key: 'comerciales', label: 'Comerciales',      count: comerciales.length },
        { key: 'empresas',    label: 'Empresas cliente', count: empresas.length },
      ];

  const list       = tab === 'admins' ? admins : (tab === 'comerciales' ? comerciales : empresas);
  const targetRole = tab === 'admins' ? 'admin' : (tab === 'comerciales' ? 'comercial' : 'empresa-cliente');
  const showTenant = isSuperadmin;

  function getSubTenantName(id) {
    return subTenants.find(s => s.id === id)?.name || '—';
  }

  return (
    <div className="up-page">
      <div className="up-header">
        <div>
          <h2 className="up-title">Usuarios</h2>
          <p className="up-sub">Gestiona los usuarios de tu organización.</p>
        </div>
        <button className="up-btn up-btn--primary" onClick={() => setModal({ targetRole })}>
          + Nuevo usuario
        </button>
      </div>

      <div className="up-tabs">
        {tabs.map(t => (
          <button key={t.key} className={`up-tab ${tab === t.key ? 'up-tab--on' : ''}`}
            onClick={() => setTab(t.key)}>
            {t.label} <span className="up-tab-count">{t.count}</span>
          </button>
        ))}
      </div>

      <div className="up-card">
        {loading ? (
          <p className="up-empty">Cargando usuarios…</p>
        ) : list.length === 0 ? (
          <p className="up-empty">No hay usuarios en esta categoría aún.</p>
        ) : (
          <table className="up-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Teléfono</th>
                {tab === 'empresas' && <th>Sub-empresa</th>}
                {showTenant        && <th>Empresa</th>}
                <th>Rol</th>
                <th>Estado</th>
                <th>Alta</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map(u => (
                <tr key={u.id} className={!u.active ? 'up-row--inactive' : ''}>
                  <td className="up-cell-name">
                    <div className="up-avatar">{(u.name || '?')[0].toUpperCase()}</div>
                    {u.name}
                  </td>
                  <td>{u.email}</td>
                  <td>{u.phone || '—'}</td>
                  {tab === 'empresas' && <td>{u.sub_tenant_id ? getSubTenantName(u.sub_tenant_id) : '—'}</td>}
                  {showTenant        && <td>{u.tenant_name || '—'}</td>}
                  <td><span className={`up-badge ${ROLE_COLOR[u.role] || ''}`}>{ROLE_LABEL[u.role] || u.role}</span></td>
                  <td>
                    {!u.email_verified
                      ? <span className="up-status up-status--pending">Pendiente</span>
                      : <span className={`up-status ${u.active ? 'up-status--on' : 'up-status--off'}`}>{u.active ? 'Activo' : 'Inactivo'}</span>
                    }
                  </td>
                  <td>{fmtDate(u.created_at)}</td>
                  <td>
                    <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                      <button className="up-edit-btn" onClick={() => setModal({ user: u, targetRole: u.role })} title="Editar">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      {/* Botón eliminar — superadmin ve todos; admin solo los que creó él */}
                      {(isSuperadmin || (isAdmin && u.created_by === auth.user?.id)) && (
                        <button
                          className="up-edit-btn"
                          title="Eliminar usuario"
                          onClick={() => handleDelete(u)}
                          style={{ color: '#dc2626' }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6"/>
                            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                          </svg>
                        </button>
                      )}
                      {/* Botón resetear contraseña — siempre visible para admins */}
                      <button className="up-edit-btn" title="Enviar enlace para restablecer contraseña"
                        disabled={resettingPwd === u.id}
                        onClick={() => handleSendReset(u.id, u.email, false)}
                        style={{ color:'#7c3aed', fontSize:13 }}>
                        {resettingPwd === u.id ? '…' : '🔑'}
                      </button>
                      <button className="up-edit-btn" title="Copiar enlace de restablecimiento"
                        disabled={resettingPwd === u.id}
                        onClick={() => handleSendReset(u.id, u.email, true)}
                        style={{ color:'#3b82f6', fontSize:13 }}>
                        🔗
                      </button>
                      {!u.email_verified && (
                        <button className="up-edit-btn" title="Reenviar email de verificación"
                          disabled={resending === u.id}
                          onClick={() => handleResend(u.id, u.email, false)}
                          style={{ color:'#f59e0b' }}>
                          {resending === u.id ? '…' : '✉'}
                        </button>
                      )}
                    </div>
                    {resendResult?.userId === u.id && (
                      <div style={{ fontSize:11, marginTop:3, color: resendResult.ok ? '#15803d' : '#dc2626' }}>
                        {resendResult.ok ? '✓ Verificación enviada' : `✗ ${resendResult.error}`}
                      </div>
                    )}
                    {resetResult?.userId === u.id && (
                      <div style={{ fontSize:11, marginTop:3, color: resetResult.ok ? '#15803d' : '#dc2626' }}>
                        {resetResult.ok ? '✓ Email de reset enviado' : `✗ ${resetResult.error}`}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {verifyPanel && (
        <div className="up-overlay" onClick={() => setVerifyPanel(null)}>
          <div className="up-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="up-modal-head">
              <h3>Enlace de verificación</h3>
              <button className="up-modal-close" onClick={() => setVerifyPanel(null)}>✕</button>
            </div>
            <div className="up-modal-body" style={{ padding:'1.25rem' }}>
              <p style={{ fontSize:14, color:'var(--muted)', marginBottom:12 }}>
                Comparte este enlace con <strong>{verifyPanel.email}</strong> para que active su cuenta:
              </p>
              <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 12px', fontSize:12, wordBreak:'break-all', color:'var(--muted)', marginBottom:12 }}>
                {verifyPanel.url}
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(verifyPanel.url); alert('¡Enlace copiado!'); }}
                style={{ background:'var(--primary)', color:'#fff', border:'none', borderRadius:8, padding:'8px 18px', fontWeight:600, fontSize:13, cursor:'pointer' }}
              >
                Copiar enlace
              </button>
            </div>
          </div>
        </div>
      )}

      {resetPanel && (
        <div className="up-overlay" onClick={() => setResetPanel(null)}>
          <div className="up-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="up-modal-head">
              <h3>Enlace de restablecimiento</h3>
              <button className="up-modal-close" onClick={() => setResetPanel(null)}>✕</button>
            </div>
            <div className="up-modal-body" style={{ padding:'1.25rem' }}>
              <p style={{ fontSize:14, color:'var(--muted)', marginBottom:12 }}>
                Comparte este enlace con <strong>{resetPanel.email}</strong> para que cree una nueva contraseña. Caduca en 1 hora.
              </p>
              <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 12px', fontSize:12, wordBreak:'break-all', color:'var(--muted)', marginBottom:12 }}>
                {resetPanel.url}
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(resetPanel.url); alert('¡Enlace copiado!'); }}
                style={{ background:'var(--primary)', color:'#fff', border:'none', borderRadius:8, padding:'8px 18px', fontWeight:600, fontSize:13, cursor:'pointer' }}
              >
                Copiar enlace
              </button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <UserModal
          user={modal.user || null}
          subTenants={subTenants}
          tenants={tenants}
          targetRole={modal.targetRole}
          isSuperadmin={isSuperadmin}
          token={token}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
